import { Directive, ElementRef, HostBinding, HostListener, Input, NgZone, OnChanges, OnInit } from '@angular/core';
import { sleep } from 'src/app/lib/sleep';
import { AnalyticsService } from 'src/app/services/analytics.service';

@Directive({
  selector: '[appButton]',
  standalone: true,
})
export class ButtonDirective implements OnInit, OnChanges {
  @Input() color: string;
  @HostBinding('class') elementClass: string;
  @Input() name: string;
  @Input() location: string;
  @Input() analyticsExtra: string;
  @Input() spacing: string;
  @Input() spacingMobile: string;
  @Input() fullWidth: string;
  @Input() fullWidthMobile: string;
  @Input() shape: string;
  @Input() size: string;
  @Input() width: '160' | '120' | '100'; // add more as needed
  @Input() sizeMobile: string;
  @Input() height: string;
  @Input() heightMobile: string;
  @Input() weight: string;
  @Input() iconButton: boolean;
  @Input() hideTextOnMobile: boolean;
  @Input() hasIcon: boolean;
  @Input() verticalIcon: boolean;
  @Input() center: boolean;
  @Input() minWidth?: string;
  @Input() alignment: 'left' | 'center' = 'center';
  @Input() mobileAlignment: 'left' | 'center';
  @Input() tabletAlignment: 'left' | 'center';
  @Input() desktopAlignment: 'left' | 'center';
  @HostListener('click', ['$event'])
  public onClick() {
    if (this.name && this.name !== 'DONTREPORT') {
      this.analytics.track('button-clicked', false, {
        name: this.name,
        location: this.location,
        extra: this.analyticsExtra,
      });
      const icon = this.getIcon();
      const text = this.el.nativeElement.textContent;
      this.analytics.setLastButtonClicked(this.name, text, icon, this.location);
    }
  }

  constructor(private analytics: AnalyticsService, private zone: NgZone, private el: ElementRef<HTMLElement>) {}

  private getIcon() {
    const icon1 = this.el.nativeElement.innerHTML.split('<app-svg')[1];
    if (!icon1) return;
    const icon2 = icon1.split('src="')[1];
    if (!icon2) return;
    const icon = icon2.split('"')[0];
    if (!icon) return;
    return icon.replace('/assets', '').replace('/icons', '');
  }

  ngOnInit() {
    this.apply();
    if (!this.name) return console.warn('Button has no name');
    if (this.name.includes(':') || this.name.includes(' ')) {
      return console.warn('Button name has invalid characters', this.name);
    }
    if (!this.location && this.name !== 'DONTREPORT') console.warn('Button has no location', this.name);
  }

  ngOnChanges() {
    this.apply();
  }

  async apply() {
    const classList: string[] = ['custom-button'];
    classList.push(`bc-${this.color || 'default'}`);
    classList.push(`shape-${this.shape || 'default'}`);
    classList.push(`size-${this.size || '2'}`);
    if (this.width) classList.push(`w-${this.width}`);
    classList.push(`size-mobile-${this.sizeMobile || this.size || '2'}`);
    classList.push(`bh-${this.height || '2'}`);
    classList.push(`bh-mobile-${this.heightMobile || this.height || '2'}`);
    classList.push(`bs-${this.spacing || '2'}`);
    classList.push(`bs-mobile-${this.spacingMobile || this.spacing || '2'}`);
    classList.push(`bw-${this.weight || 'normal'}`);
    classList.push(`button-alignment-${this.alignment}`);
    classList.push(`b-min-w-${this.minWidth}`);

    if (this.iconButton) classList.push('icon-button');
    if (this.hasIcon) classList.push('button-has-icon');
    if (this.verticalIcon) classList.push('vertical-icon');
    if (this.center) classList.push('center-button');
    if (this.hideTextOnMobile) classList.push('hide-text-on-mobile');
    if (this.fullWidth) classList.push(`full-width`);
    if (this.fullWidthMobile) classList.push(`full-width-mobile`);

    if (this.desktopAlignment) classList.push(`b-a-d-${this.desktopAlignment}`);
    if (this.tabletAlignment) classList.push(`b-a-t-${this.tabletAlignment}`);
    if (this.mobileAlignment) classList.push(`b-a-m-${this.mobileAlignment}`);

    this.elementClass = classList.join(' ');
    this.elementClass += ' block-transitions';

    this.zone.runOutsideAngular(async () => {
      await sleep(0);
      this.elementClass = classList.join(' ');
    });
  }
}
